/**
 * Spårar senaste input-modalitet (keyboard vs pointer) globalt.
 *
 * Används för att fatta a11y-beslut som "flytta fokus till rubrik efter
 * paginering" — vilket är önskvärt för tangentbords-/skärmläsaranvändare,
 * men förvirrande/störande för mus-/touch-användare som klickade på en
 * paginerings-knapp eller följde en länk.
 *
 * SSR-säker: `getLastInputModality()` returnerar `"pointer"` utan window
 * (defensiv default — ingen oönskad fokusförflyttning på server).
 */

export type InputModality = "keyboard" | "pointer";

let lastModality: InputModality = "pointer";
let installed = false;

/**
 * Tangenter som räknas som "navigations-tangentbord". Modifier-only events
 * (Shift, Ctrl etc. ensamma) ignoreras eftersom de ofta trycks utan avsikt
 * att navigera. Tab/Shift+Tab/piltangenter/Enter/Space/Esc/Home/End/PageUp/
 * PageDown är de som faktiskt indikerar tangentbordsdriven interaktion.
 */
const KEYBOARD_NAV_KEYS = new Set([
  "Tab",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Enter",
  " ",
  "Spacebar",
  "Escape",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

function onKeyDown(e: KeyboardEvent): void {
  if (!KEYBOARD_NAV_KEYS.has(e.key)) return;
  lastModality = "keyboard";
}

function onPointer(): void {
  lastModality = "pointer";
}

export function installInputModalityTracker(): () => void {
  if (typeof window === "undefined") return () => {};
  if (installed) return () => {};
  installed = true;
  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("mousedown", onPointer, true);
  window.addEventListener("pointerdown", onPointer, true);
  window.addEventListener("touchstart", onPointer, { capture: true, passive: true });
  return () => {
    window.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("mousedown", onPointer, true);
    window.removeEventListener("pointerdown", onPointer, true);
    window.removeEventListener("touchstart", onPointer, true);
    installed = false;
  };
}

export function getLastInputModality(): InputModality {
  return lastModality;
}

export function wasLastInputKeyboard(): boolean {
  return lastModality === "keyboard";
}

/** Endast för tester — återställ intern state. */
export function __resetInputModalityForTests(): void {
  lastModality = "pointer";
  installed = false;
}
